import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import $ from 'jquery';

import type { PopupData, Service } from 'webapp-types';
import { getFormattedTime } from 'webapp/utils/number';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import { Severity } from 'webapp/services/logger';

export default class BillingPopupPaymentFailedComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('logger') declare loggerService: Service.Logger;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		$(document).on('keyup.modalConfirm', (e) => {
			if (e.which === 13) {
				this.updateBillingInformation();
			}
		});

		this.loggerService.logError(
			'Payment Error popup shown',
			{
				failedPaymentCnt: this.billingService.failedPaymentCnt,
			},
			Severity.INFO,
		);
	}

	get activeUntilFormatted() {
		return getFormattedTime(this.billingService.activeUntil, 'UTC', 'MMMM Do');
	}

	get actionText() {
		let actionText;
		if (this.currentUserService.isAdmin) {
			actionText = 'please update your billing information';
		} else {
			actionText =
				'please contact your account owner or administrator to update your billing information';
		}
		return actionText;
	}

	get accountOwner() {
		return this.accountService.accountOwner;
	}

	override willDestroy() {
		$(document).off('keyup.modalConfirm');
	}

	@action
	updateBillingInformation() {
		this.navigationService.transitionToRoute('settings.billing');
		this.close();
	}

	@action
	contactUs() {
		this.currentUserService.chatWithSupport();
		if (this.currentUserService.isAdmin) {
			this.close();
		}
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="CreditCard" @size="xl" />
			Recent payment failed
		</div>
		<div class="content">
			<h3>
				Your recent Databox payment failed. This could be due to a variety of reasons
				including the payment being declined by your bank, updated credit card information
				or insufficient founds.
			</h3>
			<p>
				<strong>In order to full access your account, {{this.actionText}}.</strong>
				We will continue refreshing data until
				{{this.activeUntilFormatted}}. You must update your billing information before that
				date to ensure there are no gaps in your data.
			</p>
			<p>
				If you have any questions, please
				<a role="button" {{on "click" this.contactUs}}>Contact our support team.</a>
			</p>
			{{#if this.currentUserService.isAdmin}}
				<div class="controls2">
					<div class="controls2__right">
						<SubmitButtonComponent
							@text="Update billing information"
							@onTrigger={{this.updateBillingInformation}}
						/>
					</div>
				</div>
			{{else}}
				<div class="ownerInfo">
					<AvatarOrInitialsComponent
						@name={{this.accountOwner.name}}
						@avatar={{this.accountOwner.avatar}}
					/>
					<div class="ownerName">
						<div class="name">
							<strong>{{this.accountOwner.name}}</strong>
							(Account owner)
						</div>
						<a class="email" href="mailto:{{this.accountOwner.email}}">
							{{this.accountOwner.email}}
						</a>
					</div>
				</div>
			{{/if}}

			{{#unless this.currentUserService.isAdmin}}
				<p>
					You are logged in as
					{{this.currentUserService.model.name}}.
					<a href="/logout">Not you?</a>
				</p>
			{{/unless}}
		</div>
	</template>
}
